// eslint-disable-next-line no-restricted-imports
import { Account } from '@eih-netbroker/api-account';
import {
  Client,
  ClientSubscription,
  ExpiryType,
  LanguageType,
  Loadable,
  NonTradingDaysOfMarket,
  OrderType,
  PartnerDataResource,
  SessionResource,
  TradingSession,
  ValidOrderCombo,
  accountsAtom,
  clientSubscriptionsAtom,
  consentsAtom,
  dictionariesAtom,
  mifidMessageAtom,
  nonTradingDaysAtom,
  tradingClassificationAtom,
  unreadMessagesCountAtom,
  validOrderCombosAtom,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useResource, useResourceHeaders } from './useResource';

export const useSession = () => {
  const { getResourceHeaderWithToken } = useResourceHeaders();
  const [, setAccounts] = useAtom(accountsAtom);
  const [, setSubscriptions] = useAtom(clientSubscriptionsAtom);
  const [, setNonTradingDays] = useAtom(nonTradingDaysAtom);
  const [, setDictionaries] = useAtom(dictionariesAtom);
  const [, setConsents] = useAtom(consentsAtom);
  const [, setUnreadMessagesCount] = useAtom(unreadMessagesCountAtom);
  const [, setValidOrderCombos] = useAtom(validOrderCombosAtom);
  const [, setTradingClassification] = useAtom(tradingClassificationAtom);
  const [, setClientMifidMessage] = useAtom(mifidMessageAtom);

  const sessionResource = useResource(SessionResource);
  const partnerDataResource = useResource(PartnerDataResource);

  const mapAccountToClient = (accounts: Array<Account>): Array<Client> =>
    accounts.reduce<Array<Client>>((acc: Array<Client>, curr: Account) => {
      const clientIndex = acc.findIndex(a => a.clientId === curr.clientId);
      if (clientIndex === -1) {
        acc.push({
          clientId: curr.clientId,
          clientName: curr.clientName,
          accounts: [
            {
              accountCategory: curr.accountCategory,
              accountNumber: curr.accountNumber,
              accountProxyType: curr.accountProxyType,
              accountAlias: curr.accountAlias,
              clientName: curr.clientName,
              clientId: curr.clientId,
              depositAccountNumber: curr.depositAccountNumber,
            },
          ],
        });
      } else {
        acc[clientIndex].accounts.push({
          accountCategory: curr.accountCategory,
          accountNumber: curr.accountNumber,
          accountProxyType: curr.accountProxyType,
          accountAlias: curr.accountAlias,
          clientName: curr.clientName,
          clientId: curr.clientId,
          depositAccountNumber: curr.depositAccountNumber,
        });
      }

      return acc;
    }, []);

  const loadClientSubscriptions = useCallback(
    async (accounts: Array<Account>): Promise<Array<ClientSubscription>> => {
      try {
        const primaryClientId = accounts.find(account => account.accountProxyType === 'main')?.clientId;
        if (!primaryClientId) {
          throw new Error(`No primary client id found in accounts: ${JSON.stringify(accounts)}`);
        }
        const { data } = await partnerDataResource.getParnetSubscriptions(
          {
            clientId: primaryClientId,
          },
          getResourceHeaderWithToken(),
        );
        return data;
      } catch (err) {
        console.error(`Failed to retrieve partner subscriptions`, err);
        throw err;
      }
    },
    [partnerDataResource, getResourceHeaderWithToken],
  );

  /**
   * Calls the session microservice to get the user's accounts, consents and dictionaries and sets up the accounts
   */
  const sessionInit = useCallback(async () => {
    const sessionInitResponse = await sessionResource.sessionInit(getResourceHeaderWithToken());
    if (!sessionInitResponse.data) {
      console.error(`No data in sessionInit response`, sessionInitResponse);
      throw new Error('No data in sessionInit response');
    }

    if (sessionInitResponse.data.accounts) {
      const clientSubscriptions = await loadClientSubscriptions(sessionInitResponse.data.accounts);
      const clients: Array<Client> = mapAccountToClient(sessionInitResponse.data.accounts);
      setAccounts(
        Loadable.loaded({
          clients: clients,
        }),
      );
      setSubscriptions(clientSubscriptions);
    }

    if (sessionInitResponse.data.consents) {
      setConsents(sessionInitResponse.data.consents);
    }

    if (sessionInitResponse.data.dictionaries) {
      setDictionaries(sessionInitResponse.data.dictionaries);
    }

    if (sessionInitResponse.data.messageUnreadCount) {
      setUnreadMessagesCount({ count: Number(sessionInitResponse.data.messageUnreadCount ?? 0), updated: Date.now() });
    }

    if (sessionInitResponse.data.tradingClassifications) {
      setTradingClassification(sessionInitResponse.data.tradingClassifications);
    }

    if (sessionInitResponse.data.nonTradingDays) {
      const nonTradingDays = sessionInitResponse.data.nonTradingDays.reduce(
        (acc, next) => {
          if (!acc[next.mic]) {
            acc[next.mic] = [];
          }
          if (next.date) {
            acc[next.mic].push(next.date);
          }
          return acc;
        },
        {} as Record<string, NonTradingDaysOfMarket>,
      );

      setNonTradingDays(nonTradingDays);
    }

    if (sessionInitResponse.data.validOrderCombos) {
      const validOrderCombos = sessionInitResponse.data.validOrderCombos.reduce(
        (acc, next) => {
          const orderCombo = {
            expiryType: next.expiryTypeId as ExpiryType,
            orderType: next.orderTypeId as OrderType,
            tradingSession: next.tradingSessionId as TradingSession,
          };
          if (!acc[next.mic]) {
            acc[next.mic] = [orderCombo];
          } else {
            acc[next.mic].push(orderCombo);
          }

          return acc;
        },
        {} as Record<string, Array<ValidOrderCombo>>,
      );

      setValidOrderCombos(validOrderCombos);
    }

    if (sessionInitResponse.data.clientMiFIDMessage) {
      setClientMifidMessage(sessionInitResponse.data.clientMiFIDMessage);
    }
  }, [
    sessionResource,
    getResourceHeaderWithToken,
    setAccounts,
    setConsents,
    setDictionaries,
    setUnreadMessagesCount,
    setSubscriptions,
    setNonTradingDays,
    loadClientSubscriptions,
    setValidOrderCombos,
    setTradingClassification,
    setClientMifidMessage,
  ]);

  const changeLanguage = useCallback(
    async (languageCode: LanguageType) => {
      const changeLanguageResponse = await sessionResource.changeLanguage(
        {
          changeLanguageRequest: {
            languageCode: languageCode,
          },
        },
        {
          ...getResourceHeaderWithToken(),
          acceptLanguage: languageCode,
        },
      );

      if (changeLanguageResponse.data.dictionaries) {
        setDictionaries(changeLanguageResponse.data.dictionaries);
      }
    },
    [getResourceHeaderWithToken, setDictionaries, sessionResource],
  );

  const getUssVersions = useCallback(async () => {
    return (await sessionResource.getUssVersions(getResourceHeaderWithToken())).data;
  }, [getResourceHeaderWithToken, sessionResource]);

  return { sessionInit, changeLanguage, getUssVersions };
};
